import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { PageTitle, Spinner } from "../../components/ui/misc";
import { Panel } from "../../components/ui/Panel";
import { toast } from "../../components/ui/Toast";
import { api } from "../../lib/api/endpoints";
import type { UserRow } from "../../lib/api/models";
import { useMe } from "../../lib/auth";
import { formatDateTime } from "../../lib/format";
import { qk } from "../../lib/queries";

const failure = (u: UserRow, e: unknown) => `${u.name}: ${e instanceof Error ? e.message : "the change failed"}`;

export function UsersAdmin() {
  const me = useMe();
  const queryClient = useQueryClient();
  const { data: users, isLoading } = useQuery({ queryKey: qk.users, queryFn: api.users.list });
  const { confirm, element } = useConfirm();
  const refresh = () => queryClient.invalidateQueries({ queryKey: qk.users });
  const deactivate = async (u: UserRow) => {
    try {
      await api.users.deactivate(u.id);
      toast(`${u.name} deactivated.`, "success");
    } catch (e) {
      toast(failure(u, e), "error");
    }
    await refresh();
  };
  const reactivate = async (u: UserRow) => {
    try {
      const active = await api.users.reactivate(u.id);
      if (active) toast(`${u.name} reactivated.`, "success");
      else toast(`${u.name} reactivated, but regains access at next sign-in (needs a KD-* group).`, "warning");
    } catch (e) {
      toast(failure(u, e), "error");
    }
    await refresh();
  };
  return (
    <div>
      <PageTitle>Users</PageTitle>
      <p className="mb-6 max-w-2xl text-ink-2">
        Accounts are created on first sign-in. Roles come from the KD-Admins, KD-Editors and KD-Viewers groups in Entra
        and update at every sign-in. Deactivating ends the person's sessions at once.
      </p>
      {isLoading ? <Spinner label="Loading users…" /> : null}
      <Panel>
        <div className="overflow-x-auto">
          <table className="kd-datatable">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Last sign-in</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(users ?? []).map((u) => (
                <tr key={u.id}>
                  <td className="text-ink">{u.name}</td>
                  <td className="text-ink-2">{u.email}</td>
                  <td>{u.role}</td>
                  <td className="text-ink-3">{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : "Never"}</td>
                  <td>
                    {u.disabled ? (
                      <span className="text-orange-ink">Deactivated</span>
                    ) : u.active ? (
                      "Active"
                    ) : (
                      <span className="text-ink-3">No Klarity Docs group at last sign-in</span>
                    )}
                  </td>
                  <td className="text-right">
                    {u.id === me?.id ? null : !u.disabled ? (
                      <Button
                        size="sm"
                        variant="danger"
                        aria-label={`Deactivate ${u.name}`}
                        onClick={async () => {
                          if (
                            await confirm({
                              title: `Deactivate ${u.name}?`,
                              body: "They are signed out everywhere and cannot sign in until reactivated.",
                              confirmLabel: "Deactivate",
                              danger: true,
                            })
                          ) {
                            await deactivate(u);
                          }
                        }}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button size="sm" aria-label={`Reactivate ${u.name}`} onClick={() => void reactivate(u)}>
                        Reactivate
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      {element}
    </div>
  );
}
